import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import { AgGridReact } from '@ag-grid-community/react';
import { Grid } from '@mui/material';
import { useAgGrid } from '@tassat/ag-grid';
import { AdminRoleId, AgGridColumnDef } from '@tassat/common-definitions';
import { AccountRecord, FirmRecord, UserRole } from '@tassat/fred-definitions';
import { isEqual } from 'lodash';
import React from 'react';

import 'SharedComponents/agGrid/styles/agGridOverrides.css';

import { AG_GRID_SMALL_HEADER_HEIGHT } from 'Lib/constant';
import ActionButtonCellRenderer from 'SharedComponents/cellRenderComponent/actionButtonsCellRenderer';
import BooleanCellRenderer from 'SharedComponents/cellRenderComponent/booleanCellRenderer';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';

interface AssociatedWalletsGridProps {
    firm: FirmRecord;
    associatedWallets: AccountRecord[];
    userRole: UserRole;
}

const editableColumnDefs: (firm: Pick<FirmRecord, 'FirmId'>, associatedWallets: AccountRecord[]) => AgGridColumnDef[] = (firm: Pick<FirmRecord, 'FirmId'>, associatedWallets: AccountRecord[]) => {
    const defaultColumnDefs: AgGridColumnDef[] = [
        {
            field: 'AccountName',
            headerName: 'Account Name',
            flex: 1,
            menuTabs: ['filterMenuTab']
        },
        {
            field: 'associatedWallet',
            headerName: 'Actions',
            headerClass: 'rightAligned',
            cellClass: 'rightAlignedCell',
            cellRenderer: 'actionButtonsCellRenderer',
            cellRendererParams: {
                actionButtonOptions: {
                    hasDelete: false,
                    hasLock: false
                }
            },
            width: 100,
            menuTabs: []
        }
    ];

    return defaultColumnDefs;
};

const readonlyColumnDefs: (firm: Pick<FirmRecord, 'FirmId'>, associatedWallets: AccountRecord[]) => AgGridColumnDef[] = (firm: Pick<FirmRecord, 'FirmId'>, associatedWallets: AccountRecord[]) => {
    const defaultColumnDefs: AgGridColumnDef[] = [
        {
            field: 'AccountName',
            headerName: 'Account Name',
            menuTabs: ['filterMenuTab'],
            flex: 1
        }
    ];

    return defaultColumnDefs;
};

export function AssociatedWalletsGrid (props: AssociatedWalletsGridProps) {
    const {
        firm,
        associatedWallets,
        userRole
    } = props;

    const [features] = useFeatures();
    const [ rowData, setRowData ] = React.useState(associatedWallets);
    const [ , onGridReady ] = useAgGrid();

    React.useEffect(() => {
        if (rowData && !isEqual(rowData, associatedWallets)) {
            setRowData(associatedWallets);
        }
    }, [associatedWallets]);

    const columnDefs: AgGridColumnDef[] = React.useMemo(() => {
        const editable = userRole.AdminRoleId !== AdminRoleId.BANK_OPS_SUPERVISOR &&
            userRole.AdminRoleId !== AdminRoleId.PCG_ADMIN && !firm?.IsClosed;

        return editable ?
            editableColumnDefs({ FirmId: firm?.FirmId }, associatedWallets)
            : readonlyColumnDefs({ FirmId: firm?.FirmId }, associatedWallets);
    }, [
        firm?.FirmId,
        firm?.IsClosed,
        associatedWallets,
        userRole
    ]);

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <div
                className='ag-theme-material'
                id='associatedAccountsGrid'
                style={{ height: '420px' }}
            >
                <AgGridReact
                    animateRows={false}
                    columnDefs={columnDefs}
                    columnMenu='legacy'
                    components={{
                        booleanRenderer: BooleanCellRenderer,
                        actionButtonsCellRenderer: ActionButtonCellRenderer
                    }}
                    context={{ features }}
                    defaultColDef={{
                        resizable: true,
                        filter: true,
                        sortable: true
                    }}
                    enableCellTextSelection={true}
                    getRowId={({ data }) => data.AccountId}
                    headerHeight={AG_GRID_SMALL_HEADER_HEIGHT}
                    onGridReady={onGridReady}
                    rowData={rowData}
                    suppressCellFocus={true}
                    suppressContextMenu={true}
                />
            </div>
        </Grid>
    );
}

export default (AssociatedWalletsGrid);